import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  createStyles,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import React from 'react';
import { CheckMark } from '../../../../icons/CheckMark';
import { SmallError } from '../../../../icons/SmallError';
import { QualityScore } from '../../Quality/Quality';
import { ErrorStatus, SuccessStatus, WarningStatus } from '../../../../icons/StatusIcons';
import { toolTipContent } from '../../ToolTipsInformation/ToolTipContent';
import { InfoIcon } from '../../../../icons/InfoIcon';
import { setCodecsTestResult } from '../../CodecsTest/CodecsTestModal/CodecsTestModal';

const useStyles = makeStyles((theme) =>
  createStyles({
    modal: {
      width: '700px',
      '@media screen and (max-width: 760px)': {
        width: '70vw',
      },
    },
    content: {
      padding: '3em',
      '@media screen and (max-width: 760px)': {
        padding: '0px',
      },
    },
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500],
    },
    iconContainer: {
      display: 'flex',
      alignItems: 'stretch',
      '& svg': {
        marginRight: '0.3em',
      },
    },
    iconContainerNested: {
      marginLeft: '10px',
      display: 'flex',
      alignItems: 'stretch',
      '& svg': {
        marginRight: '0.3em',
      },
    },
    tableSell: {
      padding: '8px',
    },
    typography: {
      '@media screen and (max-width: 760px) and (min-width: 301px)': {
        width: '500px',
      },
      '@media screen and (max-width: 300px)': {
        width: '150px',
      },
    },
  })
);

let serviceStatusObj = [
  { result: 'Supported', icon: <SuccessStatus /> },
  { result: 'Particular Supported', icon: <WarningStatus /> },
  { result: 'Not Supported', icon: <ErrorStatus /> },
];

export function CodecsHistoryStatusRow({ codec, result }: { codec?: string; result: string | undefined }) {
  const classes = useStyles();
  const serviceStatus = serviceStatusObj.find((i) => i.result === result);

  return (
    <div className={classes.iconContainer}>
      {serviceStatus?.icon}
      <Typography variant="body1">{codec}</Typography>
    </div>
  );
}

const statusIcons = {
  [QualityScore.Excellent]: <SuccessStatus />,
  [QualityScore.Good]: <SuccessStatus />,
  [QualityScore.Suboptimal]: <WarningStatus />,
  [QualityScore.Poor]: <ErrorStatus />,
};

export function HistoryResultItem({ result }: { result: any }) {
  const date = new Date(result.Timestamp * 1000).toLocaleString();
  const classes = useStyles();
  const qualityScoreUDP: QualityScore = result.testResults.qualityTestResult.bitrateUDP.qualityScore;

  const qualityScoreTCP: QualityScore = result.testResults.qualityTestResult.bitrateTCP.qualityScore;
  var audioCodecTestReportUpdated = setCodecsTestResult(result.testResults.codecsTestResult.audioCodecTestReport);
  var videoCodecTestReportUpdated = setCodecsTestResult(result.testResults.codecsTestResult.videoCodecTestReport);

  return (
    <Accordion>
      <AccordionSummary>
        <Typography>{date}</Typography>
      </AccordionSummary>
      <AccordionDetails style={{ display: 'block', padding: 0 }}>
        <Grid item md={5} style={{ maxWidth: '100%' }}>
          <div style={{ marginLeft: '10px' }}>
            <div className={classes.iconContainer}>
              {result.testResults.browserTestPassed ? <CheckMark /> : <SmallError />}
              <Typography variant="body1">Device &amp; Network Setup</Typography>
            </div>
            <div className={classes.iconContainer}>
              {result.testResults.connectivityResults.connectivityTestPassed ? <CheckMark /> : <SmallError />}
              <Typography variant="body1">Connectivity</Typography>
            </div>
            <div className={classes.iconContainerNested}>
              {result.testResults.connectivityResults.sipConnectivityUSStatus ? <CheckMark /> : <SmallError />}
              <Typography variant="body1">SIP US Connectivity</Typography>
            </div>
            <div className={classes.iconContainerNested}>
              {result.testResults.connectivityResults.sipConnectivitySAStatus ? <CheckMark /> : <SmallError />}
              <Typography variant="body1">SIP SA Connectivity</Typography>
            </div>
            <div className={classes.iconContainer}>
              {result.testResults.codecsTestResult.codecsTestsPassed ? <CheckMark /> : <SmallError />}
              <Typography variant="body1">Audio/Video Codecs</Typography>
            </div>
            <div className={classes.iconContainer}>
              <Typography variant="body1" className={classes.typography}>
                <strong>Detailed Codecs support information</strong>
              </Typography>
            </div>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Typography variant="body1">
                      <strong>Status</strong>
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1">
                      <strong>Codec</strong>
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell className={classes.tableSell}>
                    <div className={classes.iconContainer}>
                      <Tooltip title={toolTipContent.audioCodec} interactive leaveDelay={250}>
                        <div>
                          <InfoIcon />
                        </div>
                      </Tooltip>
                      {result.testResults.codecsTestResult.audioCodecTestStatus ? <SuccessStatus /> : <ErrorStatus />}
                      <Typography variant="body1">Audio</Typography>
                    </div>
                  </TableCell>
                  <TableCell className={classes.tableSell}>
                    <CodecsHistoryStatusRow
                      codec={'G711'}
                      result={audioCodecTestReportUpdated?.find((i) => i.codec === 'G711')?.result}
                    />
                    <CodecsHistoryStatusRow
                      codec={'G729'}
                      result={audioCodecTestReportUpdated?.find((i) => i.codec === 'G729')?.result}
                    />
                    <CodecsHistoryStatusRow
                      codec={'G722'}
                      result={audioCodecTestReportUpdated?.find((i) => i.codec === 'G722')?.result}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className={classes.tableSell}>
                    <div className={classes.iconContainer}>
                      <Tooltip title={toolTipContent.videoCodec} interactive leaveDelay={250}>
                        <div>
                          <InfoIcon />
                        </div>
                      </Tooltip>
                      {result.testResults.codecsTestResult.videoCodecTestStatus ? <SuccessStatus /> : <ErrorStatus />}
                      <Typography variant="body1">Video</Typography>
                    </div>
                  </TableCell>
                  <TableCell className={classes.tableSell}>
                    <CodecsHistoryStatusRow
                      codec={'H264'}
                      result={videoCodecTestReportUpdated?.find((i) => i.codec === 'H264')?.result}
                    />
                    <CodecsHistoryStatusRow
                      codec={'VP8'}
                      result={videoCodecTestReportUpdated?.find((i) => i.codec === 'VP8')?.result}
                    />
                    <CodecsHistoryStatusRow
                      codec={'VP9'}
                      result={videoCodecTestReportUpdated?.find((i) => i.codec === 'VP9')?.result}
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
            <div className={classes.iconContainer} style={{ marginTop: '20px' }}>
              {result.testResults.qualityTestResult.qualityTestPassed ? <CheckMark /> : <SmallError />}
              <Typography variant="body1">Quality &amp; Performance</Typography>
            </div>
            <div className={classes.iconContainer}>
              <Typography variant="body1" className={classes.typography}>
                <strong>Detailed Codecs support information</strong>
              </Typography>
            </div>
          </div>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableSell}>
                  <Typography variant="body1">
                    <strong>Network Info</strong>
                  </Typography>
                </TableCell>
                <TableCell className={classes.tableSell}>
                  <Typography variant="body1">
                    <strong>Score</strong>
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell className={classes.tableSell}>
                  <div className={classes.iconContainer}>
                    <Tooltip title={toolTipContent.bitrate} interactive leaveDelay={250}>
                      <div>
                        <InfoIcon />
                      </div>
                    </Tooltip>
                    <Typography variant="body1">Bitrate UDP (kbps) avg/max</Typography>
                  </div>
                </TableCell>
                <TableCell className={classes.tableSell}>
                  <div className={classes.iconContainer}>
                    {statusIcons[qualityScoreUDP]}
                    <Typography variant="body1">{`${result.testResults.qualityTestResult.bitrateUDP.average} / 
                                        ${result.testResults.qualityTestResult.bitrateUDP.max} (${
                      QualityScore[result.testResults.qualityTestResult.bitrateUDP.qualityScore]
                    })`}</Typography>
                  </div>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.tableSell}>
                  <div className={classes.iconContainer}>
                    <Tooltip title={toolTipContent.bitrate} interactive leaveDelay={250}>
                      <div>
                        <InfoIcon />
                      </div>
                    </Tooltip>
                    <Typography variant="body1">Download speed</Typography>
                  </div>
                </TableCell>
                <TableCell className={classes.tableSell}>
                  <div className={classes.iconContainer}>
                    <Typography variant="body1">{result.testResults.qualityTestResult.downloadSpeed} mbps</Typography>
                  </div>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.tableSell}>
                  <div className={classes.iconContainer}>
                    <Tooltip title={toolTipContent.bitrate} interactive leaveDelay={250}>
                      <div>
                        <InfoIcon />
                      </div>
                    </Tooltip>
                    <Typography variant="body1">Upload speed</Typography>
                  </div>
                </TableCell>
                <TableCell className={classes.tableSell}>
                  <div className={classes.iconContainer}>
                    <Typography variant="body1">{result.testResults.qualityTestResult.uploadSpeed} mbps</Typography>
                  </div>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.tableSell}>
                  <div className={classes.iconContainer}>
                    <Tooltip title={toolTipContent.bitrate} interactive leaveDelay={250}>
                      <div>
                        <InfoIcon />
                      </div>
                    </Tooltip>
                    <Typography variant="body1">RTT Result</Typography>
                  </div>
                </TableCell>
                <TableCell className={classes.tableSell}>
                  <div className={classes.iconContainer}>
                    <Typography variant="body1">{result.testResults.qualityTestResult.rttResult} ms</Typography>
                  </div>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.tableSell}>
                  <div className={classes.iconContainer}>
                    <Tooltip title={toolTipContent.bitrate} interactive leaveDelay={250}>
                      <div>
                        <InfoIcon />
                      </div>
                    </Tooltip>
                    <Typography variant="body1">Jitter Result</Typography>
                  </div>
                </TableCell>
                <TableCell className={classes.tableSell}>
                  <div className={classes.iconContainer}>
                    <Typography variant="body1">{result.testResults.qualityTestResult.jitterResult} ms</Typography>
                  </div>
                </TableCell>
              </TableRow>
              {result.testResults.qualityTestResult.isTCPEnabled && (
                <TableRow>
                  <TableCell className={classes.tableSell}>
                    <div className={classes.iconContainer}>
                      <Tooltip title={toolTipContent.bitrate} interactive leaveDelay={250}>
                        <div>
                          <InfoIcon />
                        </div>
                      </Tooltip>
                      <Typography variant="body1">Bitrate TCP (kbps) avg/max</Typography>
                    </div>
                  </TableCell>
                  <TableCell className={classes.tableSell}>
                    <div className={classes.iconContainer}>
                      {statusIcons[qualityScoreTCP]}
                      <Typography variant="body1">{`${result.testResults.qualityTestResult.bitrateTCP.average} /
                                             ${result.testResults.qualityTestResult.bitrateTCP.max} (${
                        QualityScore[result.testResults.qualityTestResult.bitrateTCP.qualityScore]
                      })`}</Typography>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
}

interface HistoryResultsProps {
  isModalOpen: boolean;
  setIsModalOpen: (isModalOpen: boolean) => void;
  historyResults: any[];
}

export function HistoryResultsModal({ isModalOpen, setIsModalOpen, historyResults }: HistoryResultsProps) {
  const classes = useStyles();

  function closeModal() {
    isModalOpen = false;
    setIsModalOpen(isModalOpen);
  }

  return (
    <>
      <Dialog open={isModalOpen} onClose={closeModal} classes={{ paper: classes.modal }}>
        <DialogTitle disableTypography>
          <Typography variant="h3" className={classes.typography}>
            History results
          </Typography>
          <IconButton aria-label="close" className={classes.closeButton} onClick={closeModal}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers className={classes.content}>
          {historyResults.map((obj) => (
            <HistoryResultItem result={obj} />
          ))}
        </DialogContent>
      </Dialog>
    </>
  );
}
